import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import {Box, Skeleton, Typography, Unstable_Grid2 as Grid2} from "@mui/material";
import Button from "../../../../../../components/base/button/button";
import CreditCard from "../../../../../../components/app-specific/payment-cards/credit-card";
import CreateCreditCardDialog from "../../../../../../components/dialogs/create-credit-card";

const initNewCreditCardDialogState = {open: false, close: () => void 0, onSuccess: () => void 0}

const CustomerPaymentMethodsViewCreditCardsSection = ({customer}) => {
    const [loadingCreditCards, setLoadingCreditCards] = useState(true);
    const [loadingProviders, setLoadingProviders] = useState(true);
    const [creditCardProviders, setCreditCardProviders] = useState([]);
    const [newCreditCardDialogState, setNewCreditCardDialogState] = useState(initNewCreditCardDialogState);
    const [data, setData] = useState([]);
    const isMounted = useIsMounted();

    const loading = useMemo(() => loadingCreditCards || loadingProviders, [loadingCreditCards, loadingProviders])

    /**
     * Fetches the list of credit card providers from the server.
     * @type {(function(): Promise<void>)}
     */
    const getCreditCardProviders = useCallback(async () => {
        setLoadingProviders(true);
        const response = await Api.getPaymentProviders();
        if (!isMounted())
            return;
        setLoadingProviders(false);
        if (response?.resultFlag) {
            const data = response?.data ?? [];
            setCreditCardProviders(data);
        }
    }, [isMounted])

    /**
     * As soon as the component is mounted, fetches the list of credit card providers
     */
    useEffect(() => {
        void getCreditCardProviders();
    }, [getCreditCardProviders])

    /**
     * Fetches the list of credit cards form the server.
     * @type {() => Promise<void>}
     */
    const getCreditCards = useCallback(async () => {
        setLoadingCreditCards(true);
        const response = await Api.getCustomerCreditCardsV2(customer?.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(response?.data ?? []);
        }
        setLoadingCreditCards(false);
    }, [isMounted, customer?.id])

    /**
     * Fetches the list of gift cards for a specific user form the server. if the component is mounted and the api
     */
    useEffect(() => {
        void getCreditCards();
    }, [getCreditCards])

    /**
     * Sets a credit card as a default credit card by calling the server and if the result is successful, updates
     * the default card's state.
     * @type {(creditCard: any) => Promise<void>}
     */
    const setCreditCardAsDefault = useCallback(async (creditCard) => {
        const response = await Api.setCustomerCreditCardAsDefaultV2({
            customerId: customer?.id,
            cardId: creditCard.id
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => {
                const prevDefault = prevState.find(e => e.isDefault);
                return prevState.map(e => e.id === creditCard.id ? {
                    ...e,
                    isDefault: true
                } : e.id === prevDefault?.id ? {...e, isDefault: false} : e)
            })
        }
    }, [isMounted, customer?.id])

    /**
     * Removes a gift card from a list of gift cards by calling the server and only updating the state if the result
     * of the api call is successful.
     * @type {(creditCard: any) => Promise<void>}
     */
    const removeCreditCard = useCallback(async (creditCard) => {
        const response = await Api.removeCustomerCreditCardV2({
            customerId: customer?.id,
            cardId: creditCard.id,
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.filter(e => e.id !== creditCard.id))
        }
    }, [isMounted, customer?.id])

    /**
     * Opens the dialog to add a new credit card
     * @type {(function(): void)}
     */
    const openNewCreditCardDialog = useCallback(() => {
        setNewCreditCardDialogState({
            open: true,
            close: () => setNewCreditCardDialogState(initNewCreditCardDialogState),
            onSuccess: (creditCard) => setData(prevState => [creditCard, ...prevState])
        })
    }, [])

    const sections = useMemo(() => Object.values(data.reduce((acc, card) => {
        const groupId = card.providerName ?? "no-provider";
        acc[groupId] = {
            id: groupId,
            title: creditCardProviders.find(e => e.name === card.providerName)?.title ?? 'No Provider',
            data: [...(acc[groupId]?.data ?? []), card]
        }
        return acc;
    }, {})), [creditCardProviders, data])

    return (
        <>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}} marginBlockEnd={2}>
                <Typography variant={'h6'} fontWeight={600}>
                    {customer?.name ?? "Customer"}'s Credit Cards
                </Typography>
                <Button
                    variant={'outlined'}
                    color={'primary'}
                    disabled={loading}
                    onClick={openNewCreditCardDialog}
                >
                    Add Credit Card
                </Button>
            </Box>
            <Grid2 container spacing={2} marginBlock={0}>
                {
                    loading &&
                    <>
                        <Grid2 xs={12}>
                            <Skeleton sx={{transform: 'none'}} height={30} width={'50%'}/>
                        </Grid2>
                        {
                            Array.from({length: 3}).map((_, index) => (
                                <Grid2 xs={'auto'} key={`first-${index}`}>
                                    <Skeleton
                                        sx={{transform: 'none'}}
                                        height={270}
                                        width={312}
                                    />
                                </Grid2>
                            ))
                        }
                        <Grid2 xs={12}>
                            <Skeleton sx={{transform: 'none'}} height={30} width={'50%'}/>
                        </Grid2>
                        {
                            Array.from({length: 3}).map((_, index) => (
                                <Grid2 xs={'auto'} key={`second-${index}`}>
                                    <Skeleton
                                        sx={{transform: 'none'}}
                                        height={270}
                                        width={312}
                                    />
                                </Grid2>
                            ))
                        }
                    </>
                }
                {
                    !loading &&
                    data?.length > 0 &&
                    sections.map((entry) => (
                        <Grid2 key={entry.id} xs={12}>
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                <Typography variant={'subtitle1'} fontWeight={600}>
                                    {entry.title ?? ""}
                                </Typography>
                                <Grid2 container spacing={2}>
                                    {
                                        entry.data?.map(creditCard => (
                                            <Grid2 xs={'auto'} key={creditCard.id}>
                                                <CreditCard
                                                    withWrapper
                                                    card={creditCard}
                                                    customerId={customer.id}
                                                    setDefault={setCreditCardAsDefault}
                                                    remove={() => removeCreditCard(creditCard)}
                                                />
                                            </Grid2>
                                        ))
                                    }
                                </Grid2>
                            </Box>
                        </Grid2>
                    ))
                }
                {
                    !loading && data?.length === 0 &&
                    <Grid2 xs={12}>
                        <Typography variant={'body2'} marginBlock={10} textAlign={'center'} width={'100%'}>
                            This customer does not have any cerdit cards saved in their account.
                            Try adding one for them.
                        </Typography>
                    </Grid2>
                }
            </Grid2>
            <CreateCreditCardDialog
                customerId={customer?.id}
                providers={creditCardProviders}
                onSuccess={newCreditCardDialogState.onSuccess}
                open={newCreditCardDialogState.open}
                close={newCreditCardDialogState.close}
            />
        </>
    );
}

export default CustomerPaymentMethodsViewCreditCardsSection;